"use client";

import Image from "next/image";
import { type JSX } from "react";
import { useLocale, useTranslations } from "next-intl";
import { RichText, useSitecore } from "@sitecore-content-sdk/nextjs";

import { useStoryContext } from "contexts/StoriesContext";

import Button from "../../../atoms/Button";
import RichTextWithConsent from "../../../atoms/RichTextWithConsent";
import VideoIframe from "../../../atoms/VideoIframe";
import {
  getImageURLForCHPages,
  getLinkTargetValue,
} from "../../../utils/contentHubComponentsUtils";

import StoryResult from "./StoryResult";
import StorySolution from "./StorySolution";
import StoryTVDSavingsAndFeaturesFields from "./StoryTVDSavingsAndFeaturesFields";
import { capitalizeLanguageRegion } from "lib/utils";

export const Default = (): JSX.Element => {
  console.log("---->[StoriesDetails] Component");

  const t = useTranslations();
  const locale = useLocale();
  const localeCode = capitalizeLanguageRegion(locale);
  const { page } = useSitecore();
  const { story } = useStoryContext();

  console.log("---->[StoriesDetails] stories", story);

  if (page.mode.isEditing) {
    return (
      <div className="stories-details ch-details mx-auto bg-white w-full lg:w-(--article-container-width) px-(--spacing-sixteen)">
        <div>
          <div>
            <h1 className="text-[34px] leading-[42px] tracking-[0.4px] font-medium text-ecolab-blue lg:text-[42px] lg:leading-[48px] lg:tracking-[0.8px] mt-5 mb-2">
              Stories Title
            </h1>
            <h2 className="text-[20px] lg:text-2xl font-medium text-gray leading-[26.4px] tracking-[0.4px] lg:leading-[36px] lg:tracking-[0.8px] mt-5 mb-2">
              Stories SubTitle
            </h2>
          </div>
        </div>
      </div>
    );
  }

  const storyCustomerLogoImageUrl = getImageURLForCHPages(
    story?.StoryCustomerLogoToAsset?.results?.[0]?.assetToPublicLink?.results,
  );
  const storyHeaderImageUrl = getImageURLForCHPages(
    story?.StoryHeaderImageVideoToAsset?.results?.[0]?.assetToPublicLink
      ?.results,
  );
  const storyBGSection1ImageUrl = getImageURLForCHPages(
    story?.StoryBGSection1ToAsset?.results?.[0]?.assetToPublicLink?.results,
  );
  const learnMoreText = t.has("learn-more") ? t("learn-more") : "Learn more";

  return (
    <div
      className="stories-details ch-details"
      data-role="stories-details-component"
    >
      <div>
        <div className="mx-auto bg-white w-full lg:w-(--article-container-width) max-lg:px-4">
          <div className="mt-5 mb-3 lg:flex lg:flex-row-reverse">
            <div className="flex max-md:justify-between lg:basis-2/12 lg:gap-4 lg:flex-col">
              {storyCustomerLogoImageUrl && (
                <div className="w-[86px] lg:w-[154px] h-[48px] lg:h-[86px] mb-5 lg:order-2 object-contain">
                  <Image
                    src={storyCustomerLogoImageUrl}
                    alt={"Customer Logo"}
                    width={154}
                    height={86}
                    className="object-contain w-full h-full"
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>
              )}
              {story?.StoryType?.results[0]?.Label?.[localeCode] && (
                <div className="order-1 invisible">
                  <div className="text-[14px] text-neutral-900 tracking-normal font-medium text-center leading-[20px] rounded-full px-3.75 py-2.5 border border-neutral-700">
                    {story?.StoryType?.results[0]?.Label?.[localeCode]}
                  </div>
                </div>
              )}
            </div>
            <div className="lg:basis-10/12 mt-5">
              {story?.StoryTitle && (
                <h1 className="text-2xl leading-[36px] tracking-normal font-normal text-ecolab-blue align-middle lg:text-[32px] lg:leading-[40px] mt-0 mb-2 relative after:content-[''] after:block after:clear-both after:pt-5.75 after:w-full lg:after:w-[400px] after:border-b-2 after:border-teal">
                  {story?.StoryTitle}
                </h1>
              )}
              {story?.StorySubTitle && (
                <h2 className="text-[22px] lg:text-2xl font-normal text-charcoal-black leading-[24px] tracking-normal lg:leading-[36px] align-middle mt-5 mb-2">
                  {story?.StorySubTitle}
                </h2>
              )}
            </div>
          </div>
          {story?.TVDTotalValueDollars && (
            <div className="flex justify-between items-center mt-5 mb-5 bg-lighter-blue border border-lightning-blue p-3 lg:pl-6">
              <div className="text-[22px] text-ecolab-blue tracking-normal font-medium leading-[24px] lg:leading-[33.6px] lg:text-[28px] lg:font-bold">
                {t.has("total-value-delivered")
                  ? t("total-value-delivered")
                  : ""}
                : {story?.TVDTotalValueDollars}
              </div>
              <div>
                <Button
                  buttonType={"primary"}
                  className="inline-flex justify-center items-center m-0! ml-2.5! max-sm:text-sm font-normal leading-[24px]"
                  field={{
                    value: {
                      href: "#annual-savings",
                      text: learnMoreText,
                      target: "",
                      linktype: "internal",
                    },
                  }}
                  tabIndex={0}
                  iconName=""
                  aria-label={learnMoreText}
                />
              </div>
            </div>
          )}

          {story?.StoryHeaderVideoURL ? (
            <div className="w-full mb-5">
              <VideoIframe src={story?.StoryHeaderVideoURL} />
            </div>
          ) : (
            storyHeaderImageUrl && (
              <div className="w-full mb-5">
                <Image
                  src={storyHeaderImageUrl}
                  alt={"Story Image"}
                  width={875}
                  height={493}
                  className="object-cover w-auto max-w-full h-full rounded-md"
                  loading="eager"
                  fetchPriority="high"
                />
              </div>
            )
          )}
          {story?.BackgroundSectionHeading && (
            <h2 className="text-ecolab-blue text-[26px] font-normal leading-[28px] mb-4 align-middle">
              {story?.BackgroundSectionHeading}
            </h2>
          )}
          {story?.BackgroundSectionBodyContent && (
            <div className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content mb-5">
              <RichText
                field={{ value: story?.BackgroundSectionBodyContent }}
              />
            </div>
          )}
          {story?.StoryBGSection1Video ? (
            <div className="w-full mb-5">
              <VideoIframe src={story?.StoryBGSection1Video} />
            </div>
          ) : (
            storyBGSection1ImageUrl && (
              <div className="w-full mb-5">
                <Image
                  src={storyBGSection1ImageUrl}
                  alt={"Story Image"}
                  width={875}
                  height={493}
                  className="object-cover w-full h-full rounded-md"
                />
              </div>
            )
          )}
          {(story?.BGSectionPrimaryLink || story?.BGSectionSecondaryLink) && (
            <div className="md:flex gap-x-2.5">
              {story?.BGSectionPrimaryLink && (
                <div className="mb-4">
                  <Button
                    buttonType={"primary"}
                    className="inline-flex justify-center items-center m-0! max-sm:w-full"
                    field={{
                      value: {
                        href: story?.BGSectionPrimaryLink,
                        text:
                          story?.BGSectionPrimaryLinkText ||
                          story?.BGSectionPrimaryLink,
                        target: getLinkTargetValue(story?.BGSectionPrimaryLink),
                        linktype: "",
                      },
                    }}
                    iconName=""
                    tabIndex={0}
                    aria-label={
                      story?.BGSectionPrimaryLinkText ||
                      story?.BGSectionPrimaryLink
                    }
                  />
                </div>
              )}
              {story?.BGSectionSecondaryLink && (
                <div className="mb-4">
                  <Button
                    buttonType={"secondary"}
                    className="inline-flex justify-center items-center m-0! max-sm:w-full"
                    field={{
                      value: {
                        href: story?.BGSectionSecondaryLink,
                        text:
                          story?.BGSectionSecondaryLinkText ||
                          story?.BGSectionSecondaryLink,
                        target: getLinkTargetValue(
                          story?.BGSectionSecondaryLink,
                        ),
                        linktype: "",
                      },
                    }}
                    tabIndex={0}
                    iconName=""
                    aria-label={
                      story?.BGSectionSecondaryLinkText ||
                      story?.BGSectionSecondaryLink
                    }
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {story?.storiesSolutionData && (
          <StorySolution storySolution={story.storiesSolutionData} />
        )}
        {story?.storiesResultData && (
          <StoryResult storyResult={story.storiesResultData} />
        )}
        {story?.LegacyHtml && (
          <div className="mx-auto bg-white w-full lg:w-(--article-container-width) max-lg:px-4 mb-(--global-component-spacing)">
            <div className="text-base font-normal text-charcoal-black space-y-1 wrap-anywhere richtext-content">
              {story?.LegacyHtml?.includes("ecolab-youtube-video") ? (
                <RichTextWithConsent
                  htmlContent={story.LegacyHtml}
                  className="ch-legacy-content"
                />
              ) : (
                <RichText
                  field={{ value: story.LegacyHtml }}
                  className="ch-legacy-content"
                />
              )}
            </div>
          </div>
        )}
        {story?.storiesTVDData && (
          <StoryTVDSavingsAndFeaturesFields
            storyTVDFeature={story.storiesTVDData}
            TVDTotalValueDollars={story?.TVDTotalValueDollars}
          />
        )}
      </div>
    </div>
  );
};
